(function () {
  'use strict';

  var CONFIG = {
    // How long a scroll gesture takes to settle. 1.0 = brisk, 1.6 = languid.
    duration: 1.35,

    // Scroll distance per wheel notch. Below 1 = longer, more cinematic travel.
    wheelMultiplier: 0.9,

    // The width at which the theme switches to container scrolling.
    // Must match the theme's own breakpoint. Below it, we do nothing.
    desktopQuery: '(min-width: 990px)',

    // The theme's scroll container.
    scrollerSelector: '.page-wrapper',

    // How often to check whether the page grew (lazy images, app widgets,
    // accordions). Cheap: one scrollHeight read.
    resizeCheckMs: 250,

    // Extra gap above an anchor target, on top of the sticky header height.
    anchorGap: 16,

    // Smoothing inside the theme customizer makes editing your own store
    // fiddly. Leave true unless you are specifically previewing the effect.
    disableInThemeEditor: true,

    // Pinned to Lenis v1.x (~19kb). To drop the external request, download
    // this URL, upload it to Assets as lenis.min.js, and in theme.liquid put
    // it BEFORE the muzarah-lenis.js tag.
    lenisUrl: 'https://cdn.jsdelivr.net/npm/lenis@1/dist/lenis.min.js'
  };

  // --- Bail-out conditions -------------------------------------------------
  // Every one of these leaves plain native scrolling in place, which is fine.

  if (window.__muzarahLenis !== undefined) return;            // already loaded
  if (!('requestAnimationFrame' in window)) return;
  if (!('MutationObserver' in window)) return;
  if (!('matchMedia' in window)) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) return;

  if (CONFIG.disableInThemeEditor &&
      window.Shopify && window.Shopify.designMode) return;

  window.__muzarahLenis = null;   // claim the slot so we cannot double-init

  var desktop = window.matchMedia(CONFIG.desktopQuery);

  // --- Styles --------------------------------------------------------------
  // With a container wrapper, Lenis puts its classes on the container rather
  // than on <html>, so these are written to work either way.

  var style = document.createElement('style');
  style.setAttribute('data-muzarah-lenis', '');
  style.textContent = [
    '.lenis.lenis-smooth { scroll-behavior: auto !important; }',
    '.lenis.lenis-smooth [data-lenis-prevent] { overscroll-behavior: contain; }',
    '.lenis.lenis-stopped { overflow: hidden !important; }',
    '.lenis.lenis-scrolling iframe { pointer-events: none; }'
  ].join('\n');
  document.head.appendChild(style);

  // --- Work out what, if anything, we should attach to ---------------------
  // Returns the container element, the string 'pending' if the theme CSS has
  // not applied yet, or null if this viewport scrolls natively.

  function resolveScroller() {
    if (!desktop.matches) return null;                 // native scroll, done
    var el = document.querySelector(CONFIG.scrollerSelector);
    if (!el) return null;
    var overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    return 'pending';    // element is there but the stylesheet has not landed
  }

  // --- Load the library ----------------------------------------------------

  var loading = false;

  function loadLenis(done) {
    if (window.Lenis) return done();
    if (loading) return;
    loading = true;
    var s = document.createElement('script');
    s.src = CONFIG.lenisUrl;
    s.async = true;
    s.onload = function () { loading = false; done(); };
    s.onerror = function () { loading = false; };   // stay on native scroll
    document.head.appendChild(s);
  }

  // --- Lifecycle -----------------------------------------------------------

  var lenis = null;
  var scroller = null;
  var rafId = null;
  var sizeTimer = null;
  var observers = [];
  var lastScrollHeight = -1;
  var previousScrollBehavior = '';

  function teardown() {
    if (!lenis) return;
    cancelAnimationFrame(rafId);
    clearInterval(sizeTimer);
    observers.forEach(function (o) { o.disconnect(); });
    observers = [];
    try { lenis.destroy(); } catch (e) {}
    if (scroller) scroller.style.scrollBehavior = previousScrollBehavior;
    lenis = null;
    scroller = null;
    lastScrollHeight = -1;
    window.__muzarahLenis = null;
  }

  function build(el) {
    scroller = el;

    // The theme sets scroll-behavior:smooth on the container, which fights
    // Lenis. Turn it off while we are driving; restore it if we stand down.
    previousScrollBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';

    lenis = new window.Lenis({
      wrapper: scroller,
      content: scroller,
      duration: CONFIG.duration,
      // Exponential ease-out: quick pickup, long soft landing.
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      syncTouch: false,          // touch stays native
      wheelMultiplier: CONFIG.wheelMultiplier,
      touchMultiplier: 1.6,
      autoResize: true
    });

    window.__muzarahLenis = lenis;

    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // --- Keep the measurement honest --------------------------------------
    // The container is a fixed 100dvh, so the Lenis ResizeObserver never fires
    // when the CONTENT inside it grows. Watch scrollHeight instead.

    function remeasure() {
      if (!lenis || !scroller) return;
      var sh = scroller.scrollHeight;
      if (sh === lastScrollHeight) return;
      lastScrollHeight = sh;
      lenis.resize();
    }

    sizeTimer = setInterval(remeasure, CONFIG.resizeCheckMs);

    var contentObserver = new MutationObserver(remeasure);
    contentObserver.observe(scroller, { childList: true, subtree: true });
    observers.push(contentObserver);

    window.addEventListener('load', remeasure);
    remeasure();

    // --- Scroll lock: pause while an overlay owns the screen --------------
    // The theme rule is:
    //   html[scroll-lock], html[scroll-lock] .page-wrapper { overflow: hidden }
    // The search and quick-add modals use that attribute. The cart and menu
    // drawers do not - they are native dialog elements, so check both.

    var locked = false;
    var syncQueued = false;

    function overlayIsOpen() {
      if (document.documentElement.hasAttribute('scroll-lock')) return true;
      if (document.querySelector('dialog[open]')) return true;
      return false;
    }

    function syncLockState() {
      syncQueued = false;
      if (!lenis) return;
      var shouldLock = overlayIsOpen();
      if (shouldLock === locked) return;
      locked = shouldLock;
      if (locked) lenis.stop();
      else lenis.start();
    }

    function queueSync() {
      if (syncQueued) return;
      syncQueued = true;
      requestAnimationFrame(syncLockState);
    }

    var lockObserver = new MutationObserver(queueSync);
    lockObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open', 'scroll-lock', 'aria-modal', 'class', 'hidden']
    });
    observers.push(lockObserver);
    syncLockState();
  }

  // --- Anchor links --------------------------------------------------------
  // Bound once, guarded on lenis, so it survives rebuilds.

  function headerOffset() {
    var header = document.querySelector(
      'header-component.header, .header, header[role="banner"], sticky-header'
    );
    if (!header) return CONFIG.anchorGap;
    var position = window.getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') return CONFIG.anchorGap;
    return header.getBoundingClientRect().height + CONFIG.anchorGap;
  }

  document.addEventListener('click', function (e) {
    if (!lenis) return;
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
    var link = e.target.closest && e.target.closest('a[href*="#"]');
    if (!link || link.hasAttribute('data-lenis-ignore')) return;

    var url;
    try { url = new URL(link.href, window.location.href); } catch (err) { return; }
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (!url.hash || url.hash === '#') return;

    var target;
    try { target = document.querySelector(url.hash); } catch (err) { return; }
    if (!target) return;

    e.preventDefault();
    lenis.scrollTo(target, { offset: -headerOffset(), duration: 1.6 });
    if (window.history && history.pushState) history.pushState(null, '', url.hash);
  });

  // --- Decide, and keep deciding ------------------------------------------
  // Runs at start, on load, on breakpoint change, and a few times early on in
  // case the stylesheet is still in flight.

  var pendingTries = 0;

  function evaluate() {
    var target = resolveScroller();

    if (target === 'pending') {
      // Theme CSS has not applied yet. Try again shortly, but give up rather
      // than attach to the wrong thing - that was the v1 bug.
      if (pendingTries++ < 40) setTimeout(evaluate, 100);
      return;
    }

    if (!target) { teardown(); return; }          // native scroll territory
    if (lenis && scroller === target) return;     // already correct

    teardown();
    loadLenis(function () {
      if (window.Lenis && resolveScroller() === target) build(target);
    });
  }

  function onBreakpointChange() { pendingTries = 0; evaluate(); }

  if (desktop.addEventListener) desktop.addEventListener('change', onBreakpointChange);
  else if (desktop.addListener) desktop.addListener(onBreakpointChange);

  var onReduceChange = function (e) { if (e.matches) teardown(); };
  if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onReduceChange);
  else if (reduceMotion.addListener) reduceMotion.addListener(onReduceChange);

  // Shopify re-renders sections and swaps main on page transitions.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']
    .forEach(function (evt) { document.addEventListener(evt, evaluate); });
  window.addEventListener('load', evaluate);
  window.addEventListener('pageshow', evaluate);

  evaluate();

  // --- Diagnostics ---------------------------------------------------------
  // Run __muzarahScrollCheck() in the browser console if it misbehaves.

  window.__muzarahScrollCheck = function () {
    var el = document.querySelector(CONFIG.scrollerSelector);
    var resolved = resolveScroller();
    return {
      desktopBreakpointMatches: desktop.matches,
      scrollerResolved: resolved === el ? 'container' : String(resolved),
      lenisRunning: !!lenis,
      lenisLimit: lenis ? lenis.limit : null,
      trueLimit: el ? el.scrollHeight - el.clientHeight : null,
      inSync: lenis && el
        ? Math.abs(lenis.limit - (el.scrollHeight - el.clientHeight)) < 5
        : null
    };
  };
})();
